import _debounce from 'lodash/debounce';

import { LOOM_URI } from 'constants/routes';
import {
  EVT_ENDED,
  EVT_PAUSE,
  EVT_PLAY,
  EVT_RATE_CHANGE,
} from 'constants/videoAnalyticsEvents';
import { selectPassword } from 'selectors/shareVideo';
import { getAppSource } from 'utilities/device';
import { getGraphQLClientInsights } from 'utilities/graphql';
import * as loggerx from 'utilities/loggerx';
import { store } from 'utilities/store';

import VideoView from './engagementInsightsView.graphql';
import IntervalsHandler from './intervalsHandler';
import PlayIntervals from './playIntervals.graphql';
import PlaySegments from './playSegments.graphql';
import SegmentsHandler from './segmentsHandler';

export const KEEPALIVE_PING_MS = 5000;
export const INTERVALS_PING_MS = 250;
export const MIN_SEGMENT_LEN_S = 2;
export const DEBOUNCE_WAIT_MS = 900;
export const DEBOUNCE_MAX_WAIT_MS = 1500;

export const playSegmentsQueue = [];

let sessionId;
let videoId;
let hasView = false;
let isPlaying = false;
let isLoaded = false;
let segmentsHandler;
let intervalsHandler;
let heartbeatTimer = null;
let intervalsTimer = null;
let lastIntervalCheckTime = 0;
let debouncedSyncIntervalBuckets = () => null;

function serverSegmentsHandler(segmentData) {
  const segment = { videoId, sessionId, ...segmentData };

  loggerx.addCrumb({
    message: 'sending segment',
    context: segment,
  });

  return sendMutation(PlaySegments, { segment }).then(({ data }) => {
    const payload = data?.updateVideoPlaySegment;

    if (!payload) {
      return null;
    }

    if (payload.message) {
      loggerx.error(payload.message, {
        currentSegment: segmentsHandler.currentSegment,
        queue: segmentsHandler.queue,
      });

      return null;
    }

    // if a segment is saved to DB
    // there's need to be an associated view
    !hasView && videoView();

    return payload;
  });
}

async function serverIntervalsHandler(buckets) {
  // when the video source does not load because of errors
  // we do not handle it properly and the duration is not
  // propagated correctly to the intervalsHandler
  // until we understand and fix this issue (it's mostly on low end android devices)
  // let's bail here
  if (!buckets?.length) {
    loggerx.error(Error('Empty buckets'), {
      intervals: intervalsHandler.intervalBuckets,
      duration: intervalsHandler.duration,
    });

    return null;
  }

  const watchedIntervals = { videoId, buckets };

  loggerx.addCrumb({
    message: 'sending intervals',
    context: watchedIntervals,
  });
  const { data } = await sendMutation(PlayIntervals, { watchedIntervals });

  const payload = data?.updateVideoPlayInterval;

  if (!payload) {
    return null;
  }

  if (payload.message) {
    loggerx.error(payload.message, {
      intervals: intervalsHandler.intervalBuckets,
      duration: intervalsHandler.duration,
    });

    return null;
  }

  return payload;
}

function syncIntervals() {
  if (!hasView) {
    return;
  }

  intervalsHandler.syncBuckets();
}

export function init(
  SegmentsHandlerService = SegmentsHandler,
  IntervalsHandlerService = IntervalsHandler
) {
  segmentsHandler = new SegmentsHandlerService();
  intervalsHandler = new IntervalsHandlerService();

  loggerx.addCrumb({
    message: 'init segments listener',
  });

  debouncedSyncIntervalBuckets = _debounce(
    () => syncIntervals(),
    DEBOUNCE_WAIT_MS,
    { maxWait: DEBOUNCE_MAX_WAIT_MS }
  );
}

export function sessionStart(event) {
  sessionId = event.metadata.id;
  videoId = event.metadata.videoId;
  segmentsHandler.registerServerHandler(serverSegmentsHandler);
  intervalsHandler.registerServerHandler(serverIntervalsHandler);

  loggerx.addCrumb({
    message: 'session start',
    context: { sessionId, videoId },
  });
}

export function sessionEnd(event) {
  const { dropSession, currentTime, played } = event.metadata;
  const segments = segmentsHandler.finalize(currentTime);
  const watchedIntervals = intervalsHandler.getBucketsAsArray();
  const partialData = {
    segments,
    watchedIntervals,
    sessionId,
    videoId,
    played,
    currentTime,
  };

  const navigator = window.navigator;
  // Only sync operations can happen on sessionEnd
  // since it's called on page unload
  // graphQL cannot be used here
  const url = [LOOM_URI, '/insights-api', '/end-video-session/', videoId].join(
    ''
  );
  const data = JSON.stringify(partialData);

  if (!navigator.sendBeacon || !navigator.sendBeacon(url, data)) {
    const req = new XMLHttpRequest();

    req.open('POST', url, false);
    req.setRequestHeader('Content-Type', 'text/plain');
    req.send(data);
  }

  if (dropSession === true) {
    // kill off running timers
    heartbeatTimer && clearInterval(heartbeatTimer);
    intervalsTimer && clearInterval(intervalsTimer);
  }
}

export function keepAlive(event, data, videoRef) {
  const action = keepAliveActions[event.eventName];

  // video is not ready yet
  if (!videoRef) {
    loggerx.addCrumb({
      message: 'keepAlive: video not ready',
    });

    return;
  }

  action && action(event, data, videoRef);
}

export function videoLoaded(event, data, videoRef) {
  loggerx.addCrumb({
    message: 'videoLoaded: check',
    context: { isLoaded, duration: videoRef?.duration },
  });

  if (isLoaded) {
    return;
  }

  if (videoRef?.duration) {
    isLoaded = true;
    intervalsHandler.createBucketsForLength(videoRef.duration);
    intervalsHandler.syncBuckets();

    loggerx.addCrumb({
      message: 'videoLoaded: start intervals check',
      context: {
        isPlaying,
        time: videoRef.currentTime,
      },
    });

    // if user hit play before video was ready
    if (isPlaying) {
      startIntervalCheck(videoRef);
    }
  }
}
/**
 * For consistency with the non-ei-tracked sessions
 * we get a view event from outside.
 * To make sure segments are not let orphans,
 * we force a view (if not already happened)
 * when we send the first segment.
 *
 * Intervals are NOT sent until after a view has been marked.
 */
export function videoView() {
  if (hasView) {
    return;
  }

  return markAsViewed().then(() => {
    debouncedSyncIntervalBuckets();
    debouncedSyncIntervalBuckets.flush();
  });
}

function startIntervalCheck(videoRef, time) {
  loggerx.addCrumb({
    message: 'start intervals check',
    context: {
      lastIntervalCheckTime,
      time: videoRef.currentTime,
    },
  });

  lastIntervalCheckTime = time || videoRef.currentTime;

  // start interval
  intervalsTimer = setInterval(onIntervalCheck, INTERVALS_PING_MS, videoRef);
}

export function playSegments(event) {
  const action = playSegmentsActions[event.eventName];
  const { currentTime, playbackRate } = event.metadata;

  if (!action) {
    return;
  }

  if (event.eventName === EVT_RATE_CHANGE) {
    return action(playbackRate);
  }

  action(currentTime);
}

const playSegmentsActions = {
  [EVT_PLAY](time) {
    loggerx.addCrumb({
      message: 'play',
      context: { time, currentSegment: segmentsHandler.currentSegment },
    });

    const { end } = segmentsHandler.currentSegment || {};
    const isContinuationFromPrevious = time === end;

    if (isContinuationFromPrevious) {
      return onHeartbeat({ currentTime: time });
    }

    // create new segment
    segmentsHandler.create(time);
  },
  [EVT_PAUSE](time) {
    loggerx.addCrumb({
      message: 'pause',
      context: { time, currentSegment: segmentsHandler.currentSegment },
    });

    const { start, end } = segmentsHandler.currentSegment || {};
    const belowTreshold = time - start < MIN_SEGMENT_LEN_S || time === end;

    if (belowTreshold) {
      return;
    }

    segmentsHandler.close(time);
  },
  [EVT_RATE_CHANGE](speed = 0) {
    // ignore 0 or negative playback rates
    if (speed <= 0) {
      return;
    }

    loggerx.addCrumb({
      message: 'play rate',
      context: { speed },
    });

    segmentsHandler.speed(speed);
  },
  [EVT_ENDED]() {
    debouncedSyncIntervalBuckets.flush();
  },
};

const keepAliveActions = {
  [EVT_PLAY]: (event, data, videoRef) => {
    heartbeatTimer && clearInterval(heartbeatTimer);
    intervalsTimer && clearInterval(intervalsTimer);

    if (videoRef?.paused) {
      isPlaying = false;

      return;
    }

    isPlaying = true;

    // if video was ready before user hit play
    if (isLoaded) {
      const { currentTime } = event?.metadata || {};

      loggerx.addCrumb({
        message: 'play: start interval check',
        context: { currentTime },
      });

      startIntervalCheck(videoRef, currentTime);
    }

    heartbeatTimer = setInterval(onHeartbeat, KEEPALIVE_PING_MS, videoRef);
  },
  [EVT_PAUSE]: () => {
    isPlaying = false;
    heartbeatTimer && clearInterval(heartbeatTimer);
    intervalsTimer && clearInterval(intervalsTimer);
  },
};

function onHeartbeat(video) {
  const { currentTime = 0 } = video || {};
  const { start, end } = segmentsHandler.currentSegment || {};
  const updatedTime = Math.round(currentTime);
  const isInvalid =
    // no start?
    start == null ||
    // segment is too short
    updatedTime - start < MIN_SEGMENT_LEN_S ||
    // segment did not change
    updatedTime === end;

  if (isInvalid) {
    return;
  }

  segmentsHandler.update(updatedTime);
}

function onIntervalCheck(video) {
  const { currentTime } = video || {};
  // avoid creating an interval if the video has not yet started
  // this avoids issues with the player not being able to load the video
  // while we mark the first bucket as viewed
  const isValidTime = typeof currentTime === 'number' && currentTime > 0;
  // avoid extra checks when the video is in play status
  // but buffering/stalling (currentTime has not moved)
  const sameAsBefore = lastIntervalCheckTime === currentTime;

  if (!isValidTime || sameAsBefore) {
    return;
  }

  intervalsHandler.fillBucketAtTimestamp(lastIntervalCheckTime, currentTime);

  lastIntervalCheckTime = currentTime;

  debouncedSyncIntervalBuckets();
  loggerx.debug('debounced intervals update', { currentTime });
}

function markAsViewed() {
  if (hasView) {
    return;
  }

  hasView = true;
  const event = {
    sessionId,
    videoId,
    appSource: getAppSource(),
  };

  loggerx.addCrumb({
    message: 'video view',
  });

  const password = store ? selectPassword(store.getState()) : null;

  return sendMutation(VideoView, { event, password }).then(data => {
    loggerx.debug('video view response', { data });

    if (!data) {
      // call did not succeed
      hasView = false;
    }
  });
}

function sendMutation(mutation, variables) {
  const client = getGraphQLClientInsights();

  return client
    .mutate({
      mutation,
      variables,
    })
    .catch(err => {
      loggerx.error(err, { videoId, variables });

      return null;
    });
}

// tests helper
export function resetTimers() {
  intervalsTimer && clearInterval(intervalsTimer);
  heartbeatTimer && clearInterval(heartbeatTimer);
  isLoaded = false;
  isPlaying = false;
}
